import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, TrendingUp, Users, Calendar, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe'];

export default function AnalyticsPage({ user }) {
  const [stats, setStats] = useState(null);
  const [disciples, setDisciples] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [leaderStats, setLeaderStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIssues, setExpandedIssues] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const requests = [
        axios.get(`${API}/attendance/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/disciples`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ];

      // Super Admin gets leader stats
      if (user.role === 'super_admin') {
        requests.push(
          axios.get(`${API}/users`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/attendance/by-leader`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
      }

      const responses = await Promise.all(requests);
      setStats(responses[0].data);
      setDisciples(responses[1].data);
      
      if (user.role === 'super_admin') {
        setLeaders(responses[2].data.filter(u => u.role === 'homecell_leader'));
        setLeaderStats(responses[3].data);
      }
    } catch (error) {
      toast.error("Failed to fetch analytics data");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!stats) return;

    const report = {
      generated_at: new Date().toISOString(),
      total_disciples: disciples.length,
      attendance_stats: stats,
      generation_breakdown: getGenerationData()
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `church-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded successfully");
  };

  const getGenerationData = () => {
    const genStats = {};
    disciples.forEach((d) => {
      genStats[d.generation] = (genStats[d.generation] || 0) + 1;
    });
    return Object.entries(genStats).map(([gen, count]) => ({
      name: `Gen ${gen}`,
      value: count
    }));
  };

  const getAttendanceByDate = () => {
    if (!stats || !stats.attendance_records) return [];
    
    const dateMap = {};
    stats.attendance_records.forEach((record) => {
      if (!dateMap[record.date]) {
        dateMap[record.date] = { date: record.date, homecell: 0, church: 0 };
      }
      if (record.present) {
        if (record.attendance_type === 'homecell') {
          dateMap[record.date].homecell += 1;
        } else {
          dateMap[record.date].church += 1;
        }
      }
    });
    
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)).slice(-10);
  };

  const getAttendanceRates = () => {
    if (!stats) return [];
    return [
      {
        name: 'Homecell',
        rate: stats.homecell.total > 0 ? ((stats.homecell.present / stats.homecell.total) * 100).toFixed(1) : 0
      },
      {
        name: 'Church Service',
        rate: stats.church_service.total > 0 ? ((stats.church_service.present / stats.church_service.total) * 100).toFixed(1) : 0
      }
    ];
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const generationData = getGenerationData();
  const attendanceByDate = getAttendanceByDate();
  const attendanceRates = getAttendanceRates();

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span className="gradient-text">Analytics</span>
          </h1>
          <p className="text-gray-600">
            {user.role === 'super_admin' 
              ? 'Complete analytics across all disciples and homecell leaders' 
              : 'Visualize your discipleship progress'}
          </p>
        </div>
        <Button data-testid="download-report-button" onClick={downloadReport} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
          <Download className="mr-2 h-4 w-4" /> Download Report
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Disciples</p>
              <p className="text-3xl font-bold" data-testid="analytics-total-disciples">{disciples.length}</p>
            </div>
            <Users className="h-12 w-12 text-white/40" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Homecell Attendance</p>
              <p className="text-3xl font-bold text-gray-800">
                {stats?.homecell.present || 0}/{stats?.homecell.total || 0}
              </p>
            </div>
            <TrendingUp className="h-12 w-12 text-purple-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Church Service Attendance</p>
              <p className="text-3xl font-bold text-gray-800">
                {stats?.church_service.present || 0}/{stats?.church_service.total || 0}
              </p>
            </div>
            <Calendar className="h-12 w-12 text-indigo-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Disciples by Generation</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={generationData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {generationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold mb-4">Attendance Rate Comparison</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={attendanceRates}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="rate" fill="#667eea" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card mb-8">
        <h2 className="text-xl font-bold mb-4">Attendance Trend (Last 10 Sessions)</h2>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={attendanceByDate}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="homecell" stroke="#667eea" name="Homecell" strokeWidth={2} />
            <Line type="monotone" dataKey="church" stroke="#764ba2" name="Church Service" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Super Admin: Homecell Leader Performance */}
      {user.role === 'super_admin' && leaderStats && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Homecell Leaders Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Total Leaders</p>
                    <p className="text-3xl font-bold">{leaders.length}</p>
                  </div>
                  <Users className="h-12 w-12 text-white/40" />
                </div>
              </div>
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Avg Disciples per Leader</p>
                    <p className="text-3xl font-bold text-gray-800">
                      {leaders.length > 0 ? Math.round(disciples.length / leaders.length) : 0}
                    </p>
                  </div>
                  <TrendingUp className="h-12 w-12 text-purple-500" />
                </div>
              </div>
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Overall Attendance Rate</p>
                    <p className="text-3xl font-bold text-gray-800">
                      {stats?.homecell.total + stats?.church_service.total > 0 
                        ? Math.round(((stats.homecell.present + stats.church_service.present) / (stats.homecell.total + stats.church_service.total)) * 100)
                        : 0}%
                    </p>
                  </div>
                  <Calendar className="h-12 w-12 text-green-500" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">Leader Performance</h3>
              {Object.entries(leaderStats)
                .sort(([, a], [, b]) => b.attendance_rate - a.attendance_rate)
                .map(([leaderId, leaderData]) => (
                  <div key={leaderId} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-purple-600" />
                        <div>
                          <h4 className="font-semibold text-lg">{leaderData.leader_name}</h4>
                          <p className="text-sm text-gray-600">{leaderData.leader_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          leaderData.attendance_rate >= 75 ? 'text-green-600' : 
                          leaderData.attendance_rate >= 50 ? 'text-yellow-600' : 
                          'text-red-600'
                        }`}>
                          {leaderData.attendance_rate}%
                        </p>
                        <p className="text-xs text-gray-500">Attendance Rate</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-purple-50 p-3 rounded">
                        <p className="text-xs text-purple-600 font-medium">Disciples</p>
                        <p className="text-xl font-bold text-purple-900">{leaderData.total_disciples}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded">
                        <p className="text-xs text-green-600 font-medium">Present</p>
                        <p className="text-xl font-bold text-green-900">{leaderData.present_count}</p>
                      </div>
                      <div className="bg-red-50 p-3 rounded">
                        <p className="text-xs text-red-600 font-medium">Absent</p>
                        <p className="text-xl font-bold text-red-900">{leaderData.absent_count}</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="text-xs text-blue-600 font-medium">Total Records</p>
                        <p className="text-xl font-bold text-blue-900">{leaderData.total_attendance_records}</p>
                      </div>
                    </div>

                    {leaderData.issues && leaderData.issues.length > 0 && (
                      <div className="mt-3 bg-orange-50 border-l-4 border-orange-400 rounded">
                        <button
                          onClick={() => setExpandedIssues(prev => ({...prev, [leaderId]: !prev[leaderId]}))}
                          className="w-full p-3 flex items-center justify-between hover:bg-orange-100 transition-colors rounded"
                        >
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-orange-600" />
                            <p className="text-sm font-semibold text-orange-700">
                              ⚠️ {leaderData.issues.length} Issue{leaderData.issues.length > 1 ? 's' : ''} Detected
                            </p>
                          </div>
                          {expandedIssues[leaderId] ? (
                            <ChevronUp className="h-4 w-4 text-orange-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-orange-600" />
                          )}
                        </button>
                        
                        {expandedIssues[leaderId] && (
                          <div className="p-3 pt-0 space-y-3">
                            <ul className="text-xs text-orange-600 space-y-1 mb-3">
                              {leaderData.issues.map((issue, idx) => (
                                <li key={idx} className="font-medium">• {issue}</li>
                              ))}
                            </ul>
                            
                            {/* Low Attendance Disciples */}
                            {leaderData.problem_areas?.low_attendance && leaderData.problem_areas.low_attendance.length > 0 && (
                              <div className="bg-white rounded p-3">
                                <h5 className="text-xs font-bold text-red-700 mb-2">
                                  📉 Low Attendance Disciples (Below 50%)
                                </h5>
                                <div className="space-y-2">
                                  {leaderData.problem_areas.low_attendance.map((disciple) => (
                                    <div key={disciple.disciple_id} className="flex items-center justify-between p-2 bg-red-50 rounded text-xs">
                                      <span className="font-semibold text-gray-700">{disciple.disciple_name}</span>
                                      <div className="flex items-center gap-3">
                                        <span className="text-green-600">✓ {disciple.present_count}</span>
                                        <span className="text-red-600">✗ {disciple.absent_count}</span>
                                        <span className="font-bold text-red-700">{disciple.attendance_rate}%</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Chronic Absentees */}
                            {leaderData.problem_areas?.chronic_absentees && leaderData.problem_areas.chronic_absentees.length > 0 && (
                              <div className="bg-white rounded p-3">
                                <h5 className="text-xs font-bold text-orange-700 mb-2">
                                  🔴 Chronic Absentees (3+ Consecutive Absences)
                                </h5>
                                <div className="space-y-2">
                                  {leaderData.problem_areas.chronic_absentees.map((disciple) => (
                                    <div key={disciple.disciple_id} className="flex items-center justify-between p-2 bg-orange-50 rounded text-xs">
                                      <div>
                                        <span className="font-semibold text-gray-700">{disciple.disciple_name}</span>
                                        <p className="text-orange-600 mt-1">
                                          {disciple.consecutive_absences} consecutive absence{disciple.consecutive_absences > 1 ? 's' : ''}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <span className="font-bold text-orange-700">{disciple.attendance_rate}%</span>
                                        {disciple.last_attendance && (
                                          <p className="text-gray-500 text-xs mt-1">
                                            Last: {new Date(disciple.last_attendance).toLocaleDateString()}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}